import React, { useState, useEffect } from 'react';
import { Code2, Shield, Zap, Download, Github, Mail, WifiOff, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [visitorCount, setVisitorCount] = useState(0);
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    // Track visitor and get counts
    const fetchData = async () => {
      try {
        // Track visitor
        const visitorResponse = await axios.post(`${API}/visitor/track`);
        setVisitorCount(visitorResponse.data.visitor_count);
        
        // Get waitlist count
        const waitlistResponse = await axios.get(`${API}/waitlist/count`);
        setWaitlistCount(waitlistResponse.data.count);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to fetching counts separately
        try {
          const countResponse = await axios.get(`${API}/visitor/count`);
          setVisitorCount(countResponse.data.count);
          
          const waitlistResponse = await axios.get(`${API}/waitlist/count`);
          setWaitlistCount(waitlistResponse.data.count);
        } catch (err) {
          console.error('Error fetching counts:', err);
        }
      }
    };
    
    fetchData();
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubmitMessage('Please enter a valid email address.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const response = await axios.post(`${API}/waitlist`, { email });
      setSubmitMessage(response.data.message);
      setEmail('');
      
      // Refresh waitlist count
      const waitlistResponse = await axios.get(`${API}/waitlist/count`);
      setWaitlistCount(waitlistResponse.data.count);
    } catch (error) {
      if (error.response && error.response.data) {
        setSubmitMessage(error.response.data.message);
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <Code2 className="logo-icon" />
            <span className="logo-text">StecloAI</span>
          </div>
          <div className="nav-links">
            <a href="#home" className="nav-link">Home</a>
            <a href="#about" className="nav-link">About</a>
            <a href="#waitlist" className="nav-link">Waitlist</a>
          </div>
        </div>
      </nav>

      {/* Top Banner */}
      {/* <div className="top-banner">
        <div className="banner-content">
          <TrendingUp size={16} />
          <span className="banner-text">
            <strong>{waitlistCount}+</strong> developers already on the waitlist — and growing daily.
          </span>
        </div>
      </div> */}

      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-content">
          <div className="hero-badge">
            <WifiOff size={16} />
            <span>Work Offline. Always.</span>
          </div>
          <h1 className="hero-title">Your Local<br />Development Companion.</h1>
          <p className="hero-subtitle">
            Code smarter with assistance that runs entirely on your machine. No internet required. Your code never leaves your computer.
          </p>
          <div className="hero-cta">
            <Button onClick={scrollToWaitlist} className="btn-primary btn-large">
              Join the Waitlist
            </Button>
          </div>
          <p className="hero-platforms">
            <Download size={16} />
            Coming soon for Windows, macOS, and Linux
          </p>
          
          {/* Visual Element */}
          <div className="hero-visual">
            <div className="code-window">
              <div className="code-header">
                <div className="code-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="code-title">main.py</span>
                <div className="offline-indicator">
                  <WifiOff size={12} />
                  <span>Offline Mode</span>
                </div>
              </div>
              <div className="code-content">
                <div className="code-line">
                  <span className="code-keyword">def</span> <span className="code-function">process_data</span><span className="code-bracket">(</span><span className="code-param">items</span><span className="code-bracket">):</span>
                </div>
                <div className="code-line code-indent">
                  <span className="code-comment"># Local suggestion</span>
                  <div className="ai-suggestion">
                    <Zap size={14} />
                    <span>Add validation</span>
                  </div>
                </div>
                <div className="code-line code-indent">
                  <span className="code-keyword">return</span> <span className="code-bracket">[</span><span className="code-function">transform</span><span className="code-bracket">(</span><span className="code-param">x</span><span className="code-bracket">)</span> <span className="code-keyword">for</span> <span className="code-param">x</span> <span className="code-keyword">in</span> <span className="code-param">items</span><span className="code-bracket">]</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="about">
        <div className="container">
          <div className="about-content">
            <h2 className="section-title">Your Local Development Companion</h2>
            <p className="about-description">
              StecloAI is a privacy-first development tool that runs entirely on your local machine. 
              Get code completion, debugging help, and refactoring suggestions — all processed locally with zero internet dependency.
            </p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <WifiOff size={24} />
                </div>
                <h3 className="feature-title">Fully Offline</h3>
                <p className="feature-description">
                  Works without internet. No cloud servers, no API calls. All processing happens on your device.
                </p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <Shield size={24} />
                </div>
                <h3 className="feature-title">Absolute Privacy</h3>
                <p className="feature-description">
                  Your code remains fully yours — stored locally, never recorded, and never sent anywhere.
                </p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <Zap size={24} />
                </div>
                <h3 className="feature-title">Instant Response</h3>
                <p className="feature-description">
                  Local processing means zero network latency. Get instant code suggestions and assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section className="waitlist-section" id="waitlist">
        <div className="container">
          <div className="waitlist-content">
            <h2 className="waitlist-title">Get Early Access</h2>
            <p className="waitlist-subtitle">
              Be the first to experience truly private, offline development assistance.
            </p>
            
            <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
              <div className="form-group">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="waitlist-input"
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </div>
              {submitMessage && (
                <p className={`submit-message ${submitMessage.includes('Thanks') || submitMessage.includes('notify') ? 'success' : 'error'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <Code2 size={20} />
                <span>StecloAI</span>
              </div>
              <p className="footer-tagline">Your local development companion.</p>
            </div>
            
            <div className="footer-links">
              <a href="#github" className="footer-link">
                <Github size={20} />
                <span>GitHub</span>
              </a>
              <a href="#privacy" className="footer-link">
                <Shield size={20} />
                <span>Privacy</span>
              </a>
              <a href="#contact" className="footer-link">
                <Mail size={20} />
                <span>Contact</span>
              </a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 StecloAI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
